import React from 'react';
import { motion } from 'framer-motion';
export interface TimelineEvent {
  position: number;
  label: string;
  description?: string;
  isHighlighted?: boolean;
}
export interface TimelineProps {
  events: TimelineEvent[];
  start?: number;
  end?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function Timeline({
  events,
  start,
  end,
  label,
  mode = 'support'
}: TimelineProps) {
  const isStimulus = mode === 'stimulus';
  // Calculate min and max positions if not provided
  const minPos = start ?? Math.min(...events.map(e => e.position));
  const maxPos = end ?? Math.max(...events.map(e => e.position));
  const range = maxPos - minPos || 1; // Prevent division by zero
  // Sort events by position
  const sortedEvents = [...events].sort((a, b) => a.position - b.position);
  return <div className={`${isStimulus ? 'bg-indigo-50 rounded-xl px-4 pt-6 pb-8 border border-indigo-200' : 'bg-gradient-to-br from-indigo-50 to-violet-50 p-6 rounded-2xl border-2 border-indigo-200 shadow-sm'}`} aria-label="Tijdlijn" data-id="element-12934">
      {label && <p className={`text-sm font-semibold text-indigo-800 mb-8 text-center ${!isStimulus ? 'text-base' : ''}`} data-id="element-12935">
          {label}
        </p>}

      <div className="relative w-full max-w-2xl mx-auto mt-8 mb-16" data-id="element-12936">
        {/* Main timeline line */}
        <div className="absolute top-1/2 left-0 right-0 h-1 bg-indigo-200 -translate-y-1/2 rounded-full" data-id="element-12937" />
        <motion.div className="absolute top-1/2 left-0 h-1 bg-indigo-500 -translate-y-1/2 rounded-full origin-left" initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-12938" />

        {/* Axis Start Label */}
        <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 mt-6 text-[10px] font-bold text-indigo-400 bg-white/80 px-1 rounded" data-id="element-12939">
          {minPos}
        </div>

        {/* Axis End Label */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 mt-6 text-[10px] font-bold text-indigo-400 bg-white/80 px-1 rounded" data-id="element-12940">
          {maxPos}
        </div>

        {/* Events */}
        {sortedEvents.map((event, index) => {
        const percentage = (event.position - minPos) / range * 100;
        const isHighlighted = event.isHighlighted;
        // Stagger labels: even indices above, odd indices below
        const isAbove = index % 2 === 0;
        return <div key={index} className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center" style={{
          left: `${percentage}%`
        }} data-id="element-12941">
              {/* Event Label */}
              <motion.div className={`absolute ${isAbove ? 'bottom-6' : 'top-6'} w-max max-w-[120px] text-center`} initial={{
            opacity: 0,
            y: isAbove ? 10 : -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4 + index * 0.1,
            duration: 0.3
          }} data-id="element-12942">
                <div className="flex flex-col items-center" data-id="element-12943">
                  {!isAbove && <div className="w-px h-2 bg-indigo-200 mb-1" data-id="element-12944" />}
                  <span className={`text-xs font-bold px-2 py-1 rounded-md shadow-sm ${isHighlighted ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-900 border border-indigo-100'}`} data-id="element-12945">
                    {event.label}
                  </span>
                  {isAbove && <div className="w-px h-2 bg-indigo-200 mt-1" data-id="element-12946" />}
                </div>
              </motion.div>

              {/* Event Marker */}
              <motion.div className={`w-4 h-4 rounded-full border-2 z-10 ${isHighlighted ? 'bg-indigo-600 border-white shadow-md scale-125' : 'bg-white border-indigo-500'}`} initial={{
            scale: 0
          }} animate={{
            scale: isHighlighted ? 1.25 : 1
          }} transition={{
            delay: 0.2 + index * 0.1,
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} data-id="element-12947" />

              {/* Event Description - Only in support mode */}
              {!isStimulus && event.description && <motion.div className={`absolute ${isAbove ? 'top-6' : 'bottom-6'} w-max max-w-[140px] text-center`} initial={{
            opacity: 0,
            y: isAbove ? -10 : 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6 + index * 0.1,
            duration: 0.3
          }} data-id="element-12948">
                  <div className="flex flex-col items-center" data-id="element-12949">
                    {isAbove && <div className="w-px h-3 bg-indigo-200 mb-1" data-id="element-12950" />}
                    <span className="text-xs text-indigo-700 leading-tight bg-white/80 px-2 py-1 rounded" data-id="element-12951">
                      {event.description}
                    </span>
                    {!isAbove && <div className="w-px h-3 bg-indigo-200 mt-1" data-id="element-12952" />}
                  </div>
                </motion.div>}
            </div>;
      })}
      </div>
    </div>;
}